import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useIntl } from 'react-intl';

import { showToast } from '~/components/Shared/EcToast/showToast';
import { FormField } from '~/eds/src/components/FormField/FormField';
import { Label } from '~/eds/src/components/Label/Label';
import { Layout } from '~/eds/src/components/Layout/Layout';
import { Panel } from '~/eds/src/components/Panel/Panel';
import { Select } from '~/eds/src/components/Select/Select';
import { Text } from '~/eds/src/components/Text/Text';
import { TextArea } from '~/eds/src/components/TextArea/TextArea';
import { TextInput } from '~/eds/src/components/TextInput/TextInput';
import { Toggle } from '~/eds/src/components/Toggle/Toggle';
import { api } from '~/redux/api/createApi';
import { ERROR, SUCCESS } from '~/types/toast.types';
import { Uuid } from '~/types/Uuid';

import { Route } from './types';

const MAX_INSTRUCTIONS_LENGTH = 10000;

interface FormData {
  workflowId: Uuid;
  status: boolean;
  instructions: string;
}

interface FormErrors {
  workflowId?: string;
  instructions?: string;
}

interface RoutingTablePanelProps {
  route: Route | null;
  onClose: () => void;
  onSuccess: () => void;
}

const initialFormData: FormData = {
  workflowId: '',
  status: true,
  instructions: '',
};

export const RoutingTablePanel: React.FC<RoutingTablePanelProps> = ({
  route,
  onClose,
  onSuccess,
}) => {
  const intl = useIntl();
  const isEditing = Boolean(route);

  const [formData, setFormData] = useState<FormData>(initialFormData);
  const [errors, setErrors] = useState<FormErrors>({});

  const { data: unroutedWorkflows, isFetching: isFetchingWorkflows } =
    api.endpoints.getUnroutedWorkflows.useQuery(undefined);

  const [createRouting, { isLoading: isCreating }] =
    api.endpoints.createRouting.useMutation();
  const [updateRouting, { isLoading: isUpdating }] =
    api.endpoints.updateRouting.useMutation();

  const isLoading = isCreating || isUpdating;

  useEffect(() => {
    if (route) {
      setFormData({
        workflowId: route.workflowId,
        status: route.status === 'active',
        instructions: route.instructions || '',
      });
    } else {
      setFormData(initialFormData);
    }
    setErrors({});
  }, [route]);

  useEffect(() => {
    if (
      !route &&
      !formData.workflowId &&
      unroutedWorkflows &&
      unroutedWorkflows.length > 0
    ) {
      const firstWorkflow = unroutedWorkflows[0];
      setFormData({
        workflowId: firstWorkflow.workflowId,
        status: true,
        instructions: firstWorkflow.description || '',
      });
    }
  }, [route, formData.workflowId, unroutedWorkflows]);

  const workflowsMap = useMemo(() => {
    const map: Record<
      Uuid,
      { workflowId: Uuid; name: string; description: string | null }
    > = {};
    if (unroutedWorkflows) {
      unroutedWorkflows.forEach((workflow) => {
        map[workflow.workflowId] = workflow;
      });
    }
    return map;
  }, [unroutedWorkflows]);

  const workflowOptions = useMemo(() => {
    const options: Array<{ label: string; value: Uuid; icon: string }> = [];

    if (route) {
      options.push({
        label: route.routeName || route.workflowId,
        value: route.workflowId,
        icon: 'workflow',
      });
    }

    if (unroutedWorkflows && unroutedWorkflows.length > 0) {
      unroutedWorkflows.forEach((workflow) => {
        if (!route || workflow.workflowId !== route.workflowId) {
          options.push({
            label: workflow.name,
            value: workflow.workflowId,
            icon: 'workflow',
          });
        }
      });
    }

    return options;
  }, [unroutedWorkflows, route]);

  const handleChange = useCallback(
    (field: keyof FormData, value: string | boolean) => {
      setFormData((prev) => ({ ...prev, [field]: value }));
      if (errors[field as keyof FormErrors]) {
        setErrors((prev) => ({ ...prev, [field]: undefined }));
      }
    },
    [errors],
  );

  const handleWorkflowChange = useCallback(
    (workflowId: string | string[] | null) => {
      if (!workflowId || Array.isArray(workflowId)) {
        return;
      }

      const selectedWorkflow = workflowsMap[workflowId];

      setFormData((prev) => ({
        ...prev,
        workflowId,
        instructions: selectedWorkflow?.description || '',
      }));

      if (errors.workflowId) {
        setErrors((prev) => ({ ...prev, workflowId: undefined }));
      }
    },
    [workflowsMap, errors.workflowId],
  );

  const selectedWorkflowName = useMemo(() => {
    if (formData.workflowId) {
      if (route && route.workflowId === formData.workflowId) {
        return route.routeName;
      }
      return workflowsMap[formData.workflowId]?.name || '';
    }
    return '';
  }, [formData.workflowId, workflowsMap, route]);

  const validate = useCallback(() => {
    const newErrors: FormErrors = {};

    if (!formData.workflowId) {
      newErrors.workflowId = intl.formatMessage({
        id: 'RoutingTablePanel.workflowRequired',
        defaultMessage: 'Route name is required',
        description: 'Error message when workflow is not selected',
      });
    }

    if (!formData.instructions || !formData.instructions.trim()) {
      newErrors.instructions = intl.formatMessage({
        id: 'RoutingTablePanel.instructionsRequired',
        defaultMessage: 'Route instructions are required',
        description: 'Error message when instructions are not provided',
      });
    } else if (formData.instructions.length > MAX_INSTRUCTIONS_LENGTH) {
      newErrors.instructions = intl.formatMessage(
        {
          id: 'RoutingTablePanel.instructionsTooLong',
          defaultMessage: 'Instructions must be less than {max} characters',
          description: 'Error message when instructions exceed max length',
        },
        { max: MAX_INSTRUCTIONS_LENGTH },
      );
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }, [formData, intl]);

  const handleSubmit = useCallback(async () => {
    if (!validate()) {
      return;
    }

    const status = formData.status ? 'active' : 'inactive';

    try {
      if (isEditing && route) {
        await updateRouting({
          routingId: route.id,
          routingUpdateInput: {
            workflowId: formData.workflowId,
            routeName: route.routeName,
            status: status,
            instructions: formData.instructions,
            versionNumber: route.versionNumber,
          },
        }).unwrap();

        showToast(
          SUCCESS,
          intl.formatMessage(
            {
              id: 'RoutingTablePanel.updateSuccess',
              defaultMessage: '"{routeName}" has been updated.',
              description: 'Success message when route is updated',
            },
            { routeName: route.routeName },
          ),
        );
      } else {
        const selectedWorkflow = workflowsMap[formData.workflowId];
        const routeName = selectedWorkflow?.name || '';

        await createRouting({
          routingInput: {
            workflowId: formData.workflowId,
            routeName: routeName,
            status: status,
            instructions: formData.instructions,
          },
        }).unwrap();

        showToast(
          SUCCESS,
          intl.formatMessage(
            {
              id: 'RoutingTablePanel.createSuccess',
              defaultMessage: '"{routeName}" has been created.',
              description: 'Success message when route is created',
            },
            { routeName },
          ),
        );
      }

      onSuccess();
    } catch {
      showToast(
        ERROR,
        intl.formatMessage({
          id: 'RoutingTablePanel.saveError',
          defaultMessage: 'Failed to save route. Please try again.',
          description: 'Error message when route save fails',
        }),
      );
    }
  }, [
    validate,
    isEditing,
    route,
    formData,
    createRouting,
    updateRouting,
    intl,
    onSuccess,
    workflowsMap,
  ]);

  return (
    <Panel
      title={
        isEditing
          ? intl.formatMessage({
              id: 'RoutingTablePanel.editTitle',
              defaultMessage: 'Edit Route',
              description: 'Panel title when editing a route',
            })
          : intl.formatMessage({
              id: 'RoutingTablePanel.createTitle',
              defaultMessage: 'Create New Route',
              description: 'Panel title when creating a new route',
            })
      }
      position="right"
      mode="floating"
      width="480px"
      enableBackdrop
      hidden={{
        isHidden: false,
        onHide: onClose,
        placement: 'right',
      }}
      footer={{
        actions: [
          {
            text: intl.formatMessage({
              id: 'RoutingTablePanel.cancelButton',
              defaultMessage: 'Cancel',
              description: 'Cancel button text',
            }),
            level: 'secondary',
            onClick: onClose,
          },
          {
            text: isEditing
              ? intl.formatMessage({
                  id: 'RoutingTablePanel.saveButton',
                  defaultMessage: 'Save',
                  description: 'Save button text',
                })
              : intl.formatMessage({
                  id: 'RoutingTablePanel.createButton',
                  defaultMessage: 'Create',
                  description: 'Create button text',
                }),
            level: 'primary',
            onClick: handleSubmit,
            isLoading,
            disabled: isLoading,
          },
        ],
      }}
    >
      <Layout direction="column" spacing={6}>
        <FormField
          required
          input={Select}
          label={intl.formatMessage({
            id: 'RoutingTablePanel.routeNameLabel',
            defaultMessage: 'Route Name',
            description: 'Label for route name field',
          })}
          name="workflowId"
          inputProps={{
            options: workflowOptions,
            isLoading: isFetchingWorkflows,
            isClearable: false,
            isSearchable: true,
            placeholder: intl.formatMessage({
              id: 'RoutingTablePanel.routeNamePlaceholder',
              defaultMessage: 'Select a workflow',
              description: 'Placeholder for route name select',
            }),
            isDisabled: isEditing,
          }}
          value={formData.workflowId}
          onChange={handleWorkflowChange}
          error={errors.workflowId}
        />

        <Layout direction="row" spacing={4} align="center">
          <Label>
            {intl.formatMessage({
              id: 'RoutingTablePanel.statusLabel',
              defaultMessage: 'Route Status',
              description: 'Label for route status toggle',
            })}
          </Label>
          <Layout
            direction="row"
            spacing={2}
            align="center"
            style={{ marginLeft: 'auto' }}
          >
            <Text variant="tiny" style={{ fontSize: '12px' }}>
              {formData.status
                ? intl.formatMessage({
                    id: 'RoutingTablePanel.statusActivated',
                    defaultMessage: 'Activated',
                    description: 'Activated status text',
                  })
                : intl.formatMessage({
                    id: 'RoutingTablePanel.statusInactive',
                    defaultMessage: 'Inactive',
                    description: 'Inactive status text',
                  })}
            </Text>
            <Toggle
              name="status"
              enabled={formData.status}
              status={formData.status ? 'active' : 'inactive'}
              onToggle={(value: boolean) => handleChange('status', value)}
            />
          </Layout>
        </Layout>

        <Layout direction="column" spacing={2}>
          <Layout direction="row" spacing={1} align="center">
            <Label>
              {intl.formatMessage({
                id: 'RoutingTablePanel.instructionsLabel',
                defaultMessage: 'Route Instructions',
                description: 'Label for instructions field',
              })}
            </Label>
            <Text variant="body" style={{ color: '#d32f2f' }}>
              *
            </Text>
          </Layout>
          <Text variant="tiny" style={{ fontSize: '11px', color: '#666' }}>
            {intl.formatMessage({
              id: 'RoutingTablePanel.instructionsHelp',
              defaultMessage:
                'Workflow Description will auto-populate the instructions if there is one. Type the Instructions for the AI to accurately route the users.',
              description: 'Help text for instructions field',
            })}
          </Text>
          <TextArea
            name="instructions"
            placeholder={intl.formatMessage({
              id: 'RoutingTablePanel.instructionsPlaceholder',
              defaultMessage: 'Enter routing instructions',
              description: 'Placeholder for instructions field',
            })}
            maxLength={MAX_INSTRUCTIONS_LENGTH}
            rows={5}
            value={formData.instructions}
            onChange={(value: string | null) =>
              handleChange('instructions', value || '')
            }
            error={errors.instructions}
          />
          <Text variant="tiny" style={{ color: '#666' }}>
            {intl.formatMessage(
              {
                id: 'RoutingTablePanel.instructionsCharCount',
                defaultMessage: '{current} / {max}',
                description: 'Character count for instructions field',
              },
              {
                current: formData.instructions.length,
                max: MAX_INSTRUCTIONS_LENGTH,
              },
            )}
          </Text>
          {errors.instructions && (
            <Text variant="tiny" style={{ color: '#d32f2f' }}>
              {errors.instructions}
            </Text>
          )}
        </Layout>

        <FormField
          input={TextInput}
          label={intl.formatMessage({
            id: 'RoutingTablePanel.destinationLabel',
            defaultMessage: 'Route Destination',
            description: 'Label for route destination field',
          })}
          name="destination"
          inputProps={{
            disabled: true,
          }}
          value={selectedWorkflowName}
          onChange={() => {}}
          tooltip={intl.formatMessage({
            id: 'RoutingTablePanel.destinationTooltip',
            defaultMessage: 'Read Only',
            description: 'Tooltip for read-only route destination field',
          })}
        />
      </Layout>
    </Panel>
  );
};
